import gql from 'graphql-tag';
import CurrentCartOrderMixin from "@/mixins/CurrentCartOrderMixin";
import { getOwnedBookshelfItem } from "@/utils/bookshelfOwnership.mjs";
import { normalizeResourceId } from "@/utils/GeneralFunctions";

const GET_OWNED_BOOKS = gql`
  query getMyOwnedBooks($productIds: [String!]!) {
    getMyOwnedBooks(productIds: $productIds) {
      id
      _id
      title
      format
      readUrl
      unencryptedFilePath
    }
  }
`;

export default {
  name: 'BookshelfMixin',
  mixins: [CurrentCartOrderMixin],
  data() {
    return {
      ownedBooksLoading: 0,
      previousCartLineCount: -1,
      bookshelfRefetching: false,
    };
  },
  watch: {
    currentCartOrder: {
      handler(order) {
        const lineCount = order?.orderLines?.edges?.length ?? 0;
        const previous = this.previousCartLineCount;

        // Refetch ownership when cart items are removed so stale
        // cart-filtered results no longer make cleared items appear unowned.
        if (previous > 0 && lineCount < previous && !this.disableBookshelfOwnershipQuery) {
          this.bookshelfRefetching = true;
          this.refetchBookshelf();
        }

        this.previousCartLineCount = lineCount;
      },
      immediate: true,
    },
  },
  apollo: {
    ownedBooks: {
      query: GET_OWNED_BOOKS,
      loadingKey: 'ownedBooksLoading',
      variables() {
        return {
          productIds: this.normalizedBookshelfProductIds,
        };
      },
      update(data) {
        const seen = new Set();
        const books = [];

        for (const book of data?.getMyOwnedBooks || []) {
          const id = normalizeResourceId(book?.id);
          if (!id || seen.has(id)) continue;
          seen.add(id);
          books.push(book);
        }

        return books;
      },
      skip() {
        return this.disableBookshelfOwnershipQuery ||
          !this.$auth.isAuthenticated ||
          !this.$auth?.user?.email_verified ||
          !this.getMe ||
          this.normalizedBookshelfProductIds.length === 0;
      },
    },
  },
  computed: {
    getMe() {
      return this.$store.state.auth.getMe;
    },
    disableBookshelfOwnershipQuery() {
      return false;
    },
    bookshelfProductIds() {
      const id = this.bookData?.id || this.bookData?._id || this.bookId || this.id || this.productId;
      if (id) return [id];

      return (this.currentCartOrder?.orderLines?.edges || [])
        .map((edge) => edge?.node?.productId)
        .filter(Boolean);
    },
    normalizedBookshelfProductIds() {
      return this.normalizeBookshelfProductIds(this.bookshelfProductIds);
    },
    combinedBookshelf() {
      return this.ownedBooks || [];
    },
    bookshelfLoading() {
      return Boolean(this.ownedBooksLoading) ||
        Boolean(this.currentCartId && !this.currentCartOrderResolved);
    },
  },
  methods: {
    normalizeBookshelfProductIds(productIds) {
      const seen = new Set();
      const ids = [];

      for (const id of productIds || []) {
        const normalizedId = normalizeResourceId(id);
        if (!normalizedId || seen.has(normalizedId)) continue;
        seen.add(normalizedId);
        ids.push(normalizedId);
      }

      return ids;
    },
    checkInBookshelf(id) {
      if (this.currentCartId && !this.currentCartOrderReady) {
        return null;
      }

      // While ownership is refetching after cart removal, return null
      // to avoid stale cache showing removed items as "owned".
      if (this.bookshelfRefetching) {
        return null;
      }

      return getOwnedBookshelfItem(this.combinedBookshelf, id, this.currentCartOrder);
    },
    async refetchBookshelf(productIds = this.normalizedBookshelfProductIds) {
      try {
        if (this.disableBookshelfOwnershipQuery) return;

        const cache = this.$apollo.provider.defaultClient.cache;
        cache.evict({ fieldName: 'getMyOwnedBooks' });
        cache.gc();

        const normalizedProductIds = this.normalizeBookshelfProductIds(productIds);
        if (this.$apollo.queries.ownedBooks && normalizedProductIds.length > 0) {
          await this.$apollo.queries.ownedBooks.refetch({ productIds: normalizedProductIds });
        }
      } finally {
        this.bookshelfRefetching = false;
      }
    },
  },
}
